// eslint-disable-next-line
/// <reference types="google.maps" />

import {
  ADDRESS_COMPONENT_MAPS,
  AddressComponentType,
  AddressComponents,
  IAddressMetadata,
  IHasAddressFields,
  IManualAddressInput,
} from '@principle-theorem/principle-core/interfaces';
import { camelCase, compact, isEmpty } from 'lodash';

export class Address {
  static metadata(result: google.maps.places.PlaceResult): IAddressMetadata {
    const address = result.formatted_address;
    const location = result.geometry?.location;

    const coordinates = location
      ? { latitude: location.lat(), longitude: location.lng() }
      : undefined;

    const components = Address.transformAddressComponents(
      result.address_components ?? []
    );

    return { address, coordinates, components, isVerified: !!coordinates };
  }

  static transformAddressComponents(
    data: google.maps.GeocoderAddressComponent[]
  ): AddressComponents {
    const country = data.find((comp) => comp.types.includes('country'));
    const countryCode = country?.short_name.toLowerCase() || '';
    const countryCodeKey = countryCode as keyof typeof ADDRESS_COMPONENT_MAPS;

    const componentMap: Record<string, AddressComponentType> =
      ADDRESS_COMPONENT_MAPS[countryCodeKey] || {};

    return data.reduce((result = {}, component) => {
      const componentType = component.types[0];
      const key =
        componentMap[componentType] ||
        (camelCase(componentType) as AddressComponentType);

      result[key] = component.long_name;
      return result;
    }, {} as AddressComponents);
  }

  static format(address: IManualAddressInput): string {
    const orderedKeys: (keyof IManualAddressInput)[] = [
      AddressComponentType.StreetName,
      AddressComponentType.Subpremise,
      AddressComponentType.City,
      AddressComponentType.State,
      AddressComponentType.PostalCode,
      AddressComponentType.Country,
    ];

    const orderedValues = orderedKeys.map((key) => address[key]);
    return compact(orderedValues).join(', ');
  }

  static streetAddressFromComponents(components: AddressComponents): string {
    const streetDetails = [
      components?.[AddressComponentType.StreetNumber],
      components?.[AddressComponentType.StreetName],
    ];
    return compact(streetDetails).join(' ');
  }

  static normaliseAddress<T extends IHasAddressFields>(
    entity: T
  ): IAddressMetadata {
    const metadataAddress = entity.metadata?.address;
    return !isEmpty(metadataAddress) && metadataAddress
      ? metadataAddress
      : { address: entity.address ?? '' };
  }
}
